import * as B from '@blueprintjs/core';
import classnames from 'classnames';
import * as geojson from 'geojson';
import I from 'immutable';
import React from 'react';

import {QuickMapVectorLayer} from 'app/components/DeclarativeMap/QuickMapVectorLayers';
import DraggableMapOverlayDialog from 'app/components/MapOverlayDialog/DraggableMapOverlayDialog';
import {HfcWithHoverKey, HydratedFeatureCollection} from 'app/modules/Remote/FeatureCollection';
import SwatchColorPicker, {DEFAULT_COLORS} from 'app/pages/MonitorProjectView/SwatchColorPicker';
import {BLANK_HYDRATED_FEATURE_COLLECTION} from 'app/utils/featureCollectionUtils';
import {FeatureAttributes} from 'app/utils/featureUtils';
import {UseUploadAsOverlayOptions, useUploadAsOverlay} from 'app/utils/useUploadAsOverlay';

import cs from './QuickMapVectorDialog.module.scss';

// ~26MB, things are gonna start to feel slow at this point
const MAX_UPLOAD_SIZE_BYTES = 25 * 1024 * 1024;

type UploadableGeoJson = geojson.Feature | geojson.FeatureCollection;

function isUploadableGeoJson(v: unknown): v is UploadableGeoJson {
  if (!v || typeof v !== 'object') return false;
  const t = (v as {type?: unknown}).type;
  return t === 'Feature' || t === 'FeatureCollection';
}

function parseUploadableGeoJson(jsonText: string): UploadableGeoJson {
  const parsed: unknown = JSON.parse(jsonText);
  if (!isUploadableGeoJson(parsed)) {
    throw new Error('Uploaded file is not a GeoJSON Feature or FeatureCollection.');
  }
  return parsed;
}

function getFeaturesFromUploadableGeoJson(geoJson: UploadableGeoJson): geojson.Feature[] {
  return geoJson.type === 'FeatureCollection' ? geoJson.features : [geoJson];
}

function getHoverPropertyKey(hfc: HydratedFeatureCollection): string | null {
  return (hfc as HfcWithHoverKey).get('hoverPropertyKey') ?? null;
}

function setHoverPropertyKey(
  hfc: HydratedFeatureCollection,
  key: string | null
): HydratedFeatureCollection {
  return (hfc as HfcWithHoverKey).set('hoverPropertyKey', key);
}

const hiddenAttributeKeySet = new Set(FeatureAttributes.hiddenProperties);

function extractVisiblePropertyKeysFromUploadableGeoJson(
  geoJson: geojson.Feature | geojson.FeatureCollection,
  {featureLimit = 100}: {featureLimit?: number} = {}
): string[] {
  const keys = new Set<string>();
  for (const f of getFeaturesFromUploadableGeoJson(geoJson).slice(0, featureLimit)) {
    const props = f.properties || {};
    for (const k of Object.keys(props)) {
      if (!hiddenAttributeKeySet.has(k)) keys.add(k);
    }
  }
  return [...keys].sort((a, b) => a.localeCompare(b));
}

function getDefaultVisiblePropertyKeyFromUploadableGeoJson(
  geoJson: geojson.Feature | geojson.FeatureCollection
): string | null {
  const first = getFeaturesFromUploadableGeoJson(geoJson)[0];
  const props = first?.properties || {};
  for (const k of Object.keys(props)) {
    if (!hiddenAttributeKeySet.has(k)) return k;
  }
  return null;
}

const QuickMapVectorDialog: React.FunctionComponent<
  React.PropsWithChildren<{
    layers: QuickMapVectorLayer[];
    setLayers: (next: QuickMapVectorLayer[]) => void;
    overlayUploadOptions?: UseUploadAsOverlayOptions;
  }>
> = ({layers, setLayers, overlayUploadOptions}) => {
  const [error, setError] = React.useState<string | null>(null);
  const [isDragActive, setIsDragActive] = React.useState(false);
  const [isUploadDropzoneVisible, setIsUploadDropzoneVisible] = React.useState(layers.length === 0);
  const [savingLayerIds, setSavingLayerIds] = React.useState<Set<number>>(new Set());
  const {uploadAsOverlay} = useUploadAsOverlay(overlayUploadOptions ?? null);
  const swatchRef = React.useRef<HTMLElement | null>(null);
  const fileInputRef = React.useRef<HTMLInputElement | null>(null);
  const layerIdCounterRef = React.useRef(0);

  const updateLayer = React.useCallback(
    (id, patch) => setLayers(layers.map((l) => (l.id === id ? {...l, ...patch} : l))),
    [layers, setLayers]
  );

  const onUploadFiles = React.useCallback(
    async (files: FileList | null) => {
      if (!files?.length) return;

      setError(null);

      const nextLayers: QuickMapVectorLayer[] = [];
      for (const file of Array.from(files)) {
        if (file.size > MAX_UPLOAD_SIZE_BYTES) {
          setError(
            `"${file.name}" is ${Math.round(file.size / (1024 * 1024))}MB. Large files may be slow.`
          );
        }

        try {
          const text = await file.text();
          const geoJson = parseUploadableGeoJson(text);
          const id = ++layerIdCounterRef.current;
          const defaultHoverPropertyKey =
            getDefaultVisiblePropertyKeyFromUploadableGeoJson(geoJson);
          const features = getFeaturesFromUploadableGeoJson(geoJson);

          const hfc = (
            BLANK_HYDRATED_FEATURE_COLLECTION.merge({
              id,
              name: file.name,
              product: 'quick-map-vector',
              hoverPropertyKey: defaultHoverPropertyKey,
            }) as HydratedFeatureCollection
          ).set('features', I.fromJS(features));

          nextLayers.push({
            id,
            originalFileName: file.name,
            hfc,
            outlineColor:
              DEFAULT_COLORS[(layers.length + nextLayers.length) % DEFAULT_COLORS.length],
            isVisible: true,
          });
        } catch (e) {
          const message = e instanceof Error ? e.message : String(e);
          setError(`Could not read "${file.name}": ${message}`);
        }
      }

      if (nextLayers.length) {
        setLayers([...layers, ...nextLayers]);
        setIsUploadDropzoneVisible(false);
      }
    },
    [layers, setLayers]
  );

  const shouldShowUploadDropzone = layers.length === 0 || isUploadDropzoneVisible;

  return (
    <DraggableMapOverlayDialog
      title="Quick Map Vector Viewer"
      // vertically align icon with title text
      titleIcon={<B.Icon icon="upload" style={{marginTop: '2px'}} />}
      className={cs.dialog}
      canOutsideClickClose={true}
      insideRefs={[swatchRef]}
      // TODO: why can we only drag horizontally
      initialPosition={{top: 0, right: 50}}
    >
      <input
        className={cs.fileInput}
        type="file"
        multiple
        accept=".geojson,.json,application/geo+json,application/json"
        ref={fileInputRef}
        onChange={(ev) => onUploadFiles(ev.currentTarget.files)}
      />

      {layers.length > 0 && (
        <>
          <div className={cs.section}>
            {layers.map((layer) => {
              const keys = extractVisiblePropertyKeysFromUploadableGeoJson({
                type: 'FeatureCollection',
                features: layer.hfc.get('features').toJS() as GeoJSON.Feature[],
              });
              const hoverPropertyKey = getHoverPropertyKey(layer.hfc);
              return (
                <div key={layer.id} className={cs.layerRow}>
                  <div className={cs.layerHeader}>
                    <div className={cs.layerHeaderLeft}>
                      <B.Checkbox
                        className={cs.layerCheckbox}
                        checked={layer.isVisible}
                        onChange={() => updateLayer(layer.id, {isVisible: !layer.isVisible})}
                      />
                      <B.InputGroup
                        className={cs.layerNameInput}
                        small
                        value={layer.hfc.get('name')}
                        onChange={(ev: React.ChangeEvent<HTMLInputElement>) =>
                          updateLayer(layer.id, {
                            hfc: layer.hfc.set(
                              'name',
                              ev.currentTarget.value
                            ) as HydratedFeatureCollection,
                          })
                        }
                        placeholder="Layer name"
                      />
                    </div>
                    <div className={cs.layerHeaderColor}>
                      <SwatchColorPicker
                        color={layer.outlineColor}
                        setColor={(c) => updateLayer(layer.id, {outlineColor: c})}
                        size={18}
                        closeOnSelect
                        popoverRef={swatchRef}
                      />
                    </div>
                    <B.ButtonGroup className={cs.layerHeaderButtons}>
                      <B.Tooltip content="Remove">
                        <B.Button
                          icon="trash"
                          minimal
                          small
                          intent={B.Intent.DANGER}
                          onClick={() => {
                            setLayers(layers.filter((l) => l.id !== layer.id));
                          }}
                        />
                      </B.Tooltip>
                    </B.ButtonGroup>
                  </div>
                  <div className={classnames(cs.row, cs.configRow)}>
                    <div className={cs.rowLabel}>Hover label</div>
                    <B.HTMLSelect
                      value={hoverPropertyKey ?? ''}
                      onChange={(ev) =>
                        updateLayer(layer.id, {
                          hfc: setHoverPropertyKey(
                            layer.hfc,
                            ev.currentTarget.value || null
                          ) as HydratedFeatureCollection,
                        })
                      }
                      options={[
                        {label: '—', value: ''},
                        ...keys.map((k) => ({label: k, value: k})),
                      ]}
                      fill
                    />
                  </div>
                  <div className={cs.row}>
                    <div className={cs.rowLabel}>File Name</div>
                    <span
                      className={cs.layerOriginalFileName}
                      title={layer.originalFileName ?? layer.hfc.get('name')}
                    >
                      {layer.originalFileName ?? layer.hfc.get('name')}
                    </span>
                    {uploadAsOverlay && (
                      <B.Tooltip content="Save to Lens as overlay">
                        <B.Button
                          icon="floppy-disk"
                          minimal
                          small
                          loading={savingLayerIds.has(layer.id)}
                          onClick={async () => {
                            const name = layer.hfc.get('name').split('.')[0];
                            if (
                              window.prompt(
                                `Are you sure you want to save ${name} to Lens? Change or confirm your overlay's name to save.`,
                                name
                              )
                            ) {
                              const hoverPropertyKey = (layer.hfc as HfcWithHoverKey).get(
                                'hoverPropertyKey'
                              );
                              const featureCollection: geojson.FeatureCollection = {
                                type: 'FeatureCollection',
                                features: layer.hfc.get('features').toJS() as geojson.Feature[],
                              };
                              setSavingLayerIds((prev) => new Set(prev).add(layer.id));
                              try {
                                await uploadAsOverlay({
                                  featureCollection,
                                  name,
                                  hoverPropertyKeys: hoverPropertyKey
                                    ? [{key: hoverPropertyKey, label: hoverPropertyKey}]
                                    : [],
                                });
                              } finally {
                                setSavingLayerIds((prev) => {
                                  const next = new Set(prev);
                                  next.delete(layer.id);
                                  return next;
                                });
                              }
                            }
                          }}
                        />
                      </B.Tooltip>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
          <div className={cs.section}>
            <div className={cs.addLayersRow}>
              <B.Button
                small
                intent={B.Intent.PRIMARY}
                icon={shouldShowUploadDropzone ? 'chevron-down' : 'chevron-right'}
                onClick={() => setIsUploadDropzoneVisible((v) => !v)}
                minimal
              >
                Add another layer
              </B.Button>
            </div>
          </div>
        </>
      )}
      {shouldShowUploadDropzone && (
        <div className={cs.section}>
          <div
            className={classnames(cs.uploadDropzone, isDragActive && cs.uploadDropzoneActive)}
            onClick={() => fileInputRef.current?.click()}
            onDragEnter={(ev) => {
              ev.preventDefault();
              ev.stopPropagation();
              setIsDragActive(true);
            }}
            onDragOver={(ev) => {
              ev.preventDefault();
              ev.stopPropagation();
              setIsDragActive(true);
            }}
            onDragLeave={(ev) => {
              ev.preventDefault();
              ev.stopPropagation();
              setIsDragActive(false);
            }}
            onDrop={(ev) => {
              ev.preventDefault();
              ev.stopPropagation();
              setIsDragActive(false);
              void onUploadFiles(ev.dataTransfer.files);
            }}
            role="button"
            tabIndex={0}
            onKeyDown={(ev) => {
              if (ev.key === 'Enter' || ev.key === ' ') {
                ev.preventDefault();
                fileInputRef.current?.click();
              }
            }}
          >
            <B.Icon icon="cloud-upload" size={44} className={cs.uploadDropzoneIcon} />
            <div className={cs.uploadDropzoneText}>
              <div>Drag files here or</div>
              <B.Button
                minimal
                outlined
                intent={B.Intent.PRIMARY}
                onClick={(ev) => {
                  ev.stopPropagation();
                  fileInputRef.current?.click();
                }}
              >
                Browse Files
              </B.Button>
            </div>
          </div>
        </div>
      )}
      {error && <div className={classnames(cs.row, cs.error)}>{error}</div>}
    </DraggableMapOverlayDialog>
  );
};

export default QuickMapVectorDialog;
